import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const CRITERIA = [
    {
        id: 'structure',
        title: 'Structure (0–100)',
        text: 'Le CV a-t-il les blocs attendus (identité, compétences, formations, expériences) dans un ordre lisible ? Un ATS et un recruteur doivent retrouver ces sections sans tableau complexe ni colonnes décoratives.',
    },
    {
        id: 'clarte',
        title: 'Clarté (0–100)',
        text: 'Les phrases sont-elles concrètes, sans fautes, sans jargon vide ? Une mission « gestion de projet » sans objet ni résultat baisse cette note. Une phrase courte avec un verbe d’action la monte.',
    },
    {
        id: 'motsCles',
        title: 'Mots-clés (0–100)',
        text: 'Les termes du secteur (et de l’offre, en mode matching) apparaissent-ils dans les expériences, pas dans une liste artificielle ? C’est le critère le plus proche d’un filtre ATS.',
    },
    {
        id: 'secteur',
        title: 'Secteur (0–100)',
        text: 'Le profil est-il cohérent avec le métier visé ? Un CV commercial noté comme un CV développeur sera pénalisé ici, même s’il est bien rédigé.',
    },
    {
        id: 'quantification',
        title: 'Quantification (0–100)',
        text: 'Y a-t-il des chiffres : volume, délai, budget, taux, taille d’équipe ? Sans preuve mesurable, le CV reste une liste de tâches. Ce critère est souvent le plus faible.',
    },
];

const Methode = () => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        headline: 'Méthode de notation des CV — CV Analyse',
        description:
            'Comment CV Analyse calcule un score sur 100 : moyenne de 5 critères (structure, clarté, mots-clés, secteur, quantification).',
        inLanguage: 'fr-FR',
        author: { '@type': 'Organization', name: 'CV Analyse' },
        publisher: { '@type': 'Organization', name: 'CV Analyse' },
        mainEntityOfPage: 'https://www.cv-analyse.fr/methode',
    };

    return (
        <div className="min-h-screen bg-slate-200 pt-16">
            <Helmet>
                <title>Méthode de notation des CV — CV Analyse</title>
                <meta
                    name="description"
                    content="Le score CV Analyse est la moyenne de 5 notes sur 100 : structure, clarté, mots-clés, secteur, quantification. Pas une note magique."
                />
                <link rel="canonical" href="https://www.cv-analyse.fr/methode" />
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <div className="bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
                    <p className="text-sm text-slate-400 mb-3">
                        <Link to="/" className="hover:text-white">Accueil</Link>
                        {' / '}
                        Méthode
                    </p>
                    <h1 className="text-3xl md:text-4xl font-semibold mb-3">
                        Comment CV Analyse note un CV
                    </h1>
                    <p className="text-slate-300 leading-relaxed">
                        Le score global est un entier de 0 à 100. Ce n’est pas une somme de points, ni une note sur 20.
                        C’est la moyenne arrondie de cinq critères, chacun noté de 0 à 100.
                    </p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Formule</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">
                        Score = arrondi de (structure + clarté + mots-clés + secteur + quantification) / 5.
                    </p>
                    <p className="text-slate-600 leading-relaxed">
                        Un critère « correct » pour un CV de candidat sérieux se situe souvent entre 70 et 85, pas entre 14 et 17.
                        Si un outil affiche 16/20 sur un axe, ce n’est pas notre échelle.
                    </p>
                </section>

                {CRITERIA.map((item) => (
                    <section key={item.id} className="card-surface p-6 sm:p-8">
                        <h2 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h2>
                        <p className="text-slate-600 leading-relaxed">{item.text}</p>
                    </section>
                ))}

                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Ce que le score n’est pas</h2>
                    <ul className="text-slate-600 space-y-2 list-disc pl-5 leading-relaxed">
                        <li>Pas une garantie d’entretien, ni un jugement sur la personne.</li>
                        <li>Pas un ATS recruteur : on simule la lisibilité, on ne postule pas à votre place.</li>
                        <li>Pas un classement public de candidats. Les CV restent confidentiels (RGPD).</li>
                    </ul>
                </section>

                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Données agrégées</h2>
                    <p className="text-slate-600 leading-relaxed mb-4">
                        Le <Link to="/barometre" className="text-blue-700 font-medium">baromètre</Link> publie des moyennes anonymes par secteur,
                        uniquement quand l’échantillon d’un secteur compte au moins 5 analyses. Aucun nom, fichier ou texte de CV n’est exposé.
                    </p>
                    <p className="text-slate-600 leading-relaxed">
                        Formats lus : PDF texte et Word (.docx). Un PDF Canva ou scanné, sans texte sélectionnable, ne peut pas être noté.
                    </p>
                </section>

                <section className="cta-band text-center">
                    <h2 className="text-xl font-semibold mb-2">Voir le score de votre CV</h2>
                    <p className="text-slate-300 text-sm mb-5">Score et 3 conseils offerts — rapport complet dès 5 €.</p>
                    <Link to="/" className="btn-primary">Voir mon score</Link>
                </section>
            </div>
        </div>
    );
};

export default Methode;
